import React, { useState, useEffect, useRef } from "react";
import { HexColorPicker } from "react-colorful";
import { displayToast } from "../utils/displayToast";
import { createTransaction, updateTransaction } from "../apis/transactionApi";
import { fetchMenus } from "../apis/menuApi";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import "../styles/components/Transaction.css";

const CustomSelect = ({ options, value, onChange, placeholder, disabled }) => {
  const wrapperRef = useRef(null);
  const listRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [openUp, setOpenUp] = useState(false);
  const selectedLabel = options.find((o) => o.value === value)?.label || "";

  useEffect(() => {
    function handleOutside(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener("pointerdown", handleOutside);
    return () => document.removeEventListener("pointerdown", handleOutside);
  }, []);

  useEffect(() => {
    if (!open) return;
    const wrap = wrapperRef.current;
    const list = listRef.current;
    if (!wrap || !list) return;
    const rect = wrap.getBoundingClientRect();
    const viewportH = window.innerHeight;
    const spaceBelow = viewportH - rect.bottom;
    const spaceAbove = rect.top;
    const margin = 12;
    const availableBelow = Math.max(0, spaceBelow - margin);
    const availableAbove = Math.max(0, spaceAbove - margin);
    if (availableBelow < 200 && availableAbove > availableBelow) {
      setOpenUp(true);
      list.style.maxHeight = `${Math.min(availableAbove, 360)}px`;
    } else {
      setOpenUp(false);
      list.style.maxHeight = `${Math.min(
        availableBelow || viewportH - rect.top - 80,
        360
      )}px`;
    }
    setTimeout(() => {
      list && (list.style.willChange = "transform");
    }, 10);
  }, [open]);
  const handleToggle = () => {
    if (!disabled) {
      setOpen((v) => !v);
    }
  };
  const handleSelect = (val) => {
    if (typeof onChange === "function") {
      let e = { target: { value: val } };
      onChange(e);
    }
    setOpen(false);
  };
  return (
    <div className="transaction-select-wrapper" ref={wrapperRef}>
      <button
        type="button"
        className={`transaction-select-toggle ${open ? "open" : ""} ${disabled ? "disabled" : ""}`}
        onClick={handleToggle}
        aria-haspopup="listbox"
        aria-expanded={open}
        disabled={disabled}>
        <span
          className={`transaction-select-value ${!selectedLabel ? "placeholder" : ""}`}
        >
          {selectedLabel || placeholder}
        </span>
        <span className="transaction-select-caret" />
      </button>
      <ul
        ref={listRef}
        className={`transaction-select-list ${open ? "visible" : ""} ${openUp ? "open-up" : "open-down"}`}
        role="listbox"
        tabIndex={-1}
      >
        {options.map((opt) => (
          <li
            key={opt.value || opt.label}
            role="option"
            aria-selected={value === opt.value}
            className={`transaction-select-option ${value === opt.value ? "selected" : ""}`}
            onClick={() => handleSelect(opt.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleSelect(opt.value);
              }
            }}
            tabIndex={0}
          >
            {opt.label}
          </li>
        ))}
      </ul>
    </div>
  );
};
const CUSTOMER_TYPES = ["customer", "vendor"];

const Transaction = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const popover = useRef();
  const buttonRef = useRef();
  const [isOpen, setIsOpen] = useState(false);
  const [transactionOptions, setTransactionOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSavingTransaction, setIsSavingTransaction] = useState(false);
  const [error, setError] = useState(null);

  const isViewMode = location.state?.viewMode === true;

  const initialState = {
    type: "transaction",
    name: "",
    tran_code: "",
    color_code: "",
    ref_code: "",
    document_file_name: "",
    is_auto_transaction_no: false,
    is_back_dated_entry_allowed: false,
    customer_type: "customer",
    filter_data: [],
    is_pick_up_mandatory: false,
    pick_up_transactions: [],
    pick_up_data: [],
    dynamic_transaction: [],
    status: "active",
    head_view_fields: [],
    pick_up_head_view_fields: [],
    pick_up_item_view_fields: [],
    is_approval_mandatory: false,
  };

  const arrayFields = [
    "pick_up_item_view_fields",
    "pick_up_head_view_fields",
    "pick_up_transactions",
    "pick_up_data",
    "dynamic_transaction",
    "head_view_fields",
    "filter_data",
  ];

  const [formData, setFormData] = useState(() => {
    const transaction = location.state?.transaction;

    if (!transaction) return initialState;

    const normalized = {
      ...initialState,
      ...transaction,
      customer_type: transaction.customer_type || "customer",
    };

    arrayFields.forEach((key) => {
      normalized[key] = Array.isArray(transaction[key])
        ? transaction[key].filter(Boolean)
        : [];
    });

    return normalized;
  });
  const [inputValue, setInputValue] = useState(formData.color_code || "");

  useEffect(() => {
    setInputValue(formData.color_code || "");
  }, [formData.color_code]);

  useEffect(() => {
    fetchMenuOptions();
  }, []);

  const handleClickOutside = (event) => {
    if (
      popover.current &&
      !popover.current.contains(event.target) &&
      !buttonRef.current.contains(event.target)
    ) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleButtonClick = (e) => {
    e.preventDefault();
    setIsOpen(!isOpen);
  };

  const handleColorInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);

    if (/^#[0-9A-Fa-f]{6}$/.test(newValue)) {
      handleBasicInfoChange({
        target: {
          name: "color_code",
          value: newValue,
        },
      });
    }
  };

  const handleColorPickerChange = (newColor) => {
    setInputValue(newColor);
    handleBasicInfoChange({
      target: {
        name: "color_code",
        type: "text",
        value: newColor,
      },
    });
  };

  const handleColorInputBlur = () => {
    if (!/^#[0-9A-Fa-f]{6}$/.test(inputValue)) {
      setInputValue(formData.color_code || "");
    }
  };

  const fetchMenuOptions = async () => {
    try {
      const response = await fetchMenus();
      if (response && response.items) {
        const menuItems = response.items
          .flatMap((item) => item.child_menu || [])
          .filter((menu) => menu.type === "transaction")
          .map((menu) => ({
            code: menu.menu_code,
            name: menu.name,
          }));

        setTransactionOptions(menuItems);
      }
    } catch (error) {
      setError("Failed to load menu options");
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading transaction options...</p>
      </div>
    );
  }

  if (error || transactionOptions.length === 0) {
    return (
      <div className="no-transactions-container">
        <div className="no-transactions-content">
          <h2>No Transactions Available</h2>
          <p>Please add menus to create transactions.</p>
        </div>
      </div>
    );
  }

  const defaultItems = {
    filter_data: {
      table_type: "",
      field_name: "",
      title: "",
      operation: "=",
      order_no: 1,
    },
    pick_up_transactions: {
      tran_code: "",
      tran_name: "",
    },
    pick_up_data: {
      tran_code: "",
      tran_name: "",
      head_fields: [
        {
          field_name: "",
          copy_to_field_name: "",
          copy_to_table_type: "head",
        },
      ],
      item_fields: [
        {
          field_name: "",
          copy_to_field_name: "",
          copy_to_table_type: "item",
        },
      ],
    },
    dynamic_transaction: {
      tran_code: "",
      tran_name: "",
      head_fields: [
        {
          field_name: "",
          copy_to_field_name: "",
          copy_to_table_type: "head",
        },
      ],
      item_fields: [
        {
          field_name: "",
          copy_to_field_name: "",
          copy_to_table_type: "item",
        },
      ],
    },
    head_fields: {
      field_name: "",
      copy_to_field_name: "",
      copy_to_table_type: "head",
    },
    item_fields: {
      field_name: "",
      copy_to_field_name: "",
      copy_to_table_type: "item",
    },
    head_view_fields: {
      field_name: "",
      display_name: "",
      is_internal: false,
      flex: 0,
    },
    pick_up_head_view_fields: {
      field_name: "",
      display_name: "",
      is_internal: false,
    },
    pick_up_item_view_fields: {
      field_name: "",
      display_name: "",
      is_internal: false,
    },
  };

  const handleBasicInfoChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleArrayFieldChange = (section, index, field, value) => {
    setFormData((prev) => {
      if (!prev[section] || !Array.isArray(prev[section])) return prev;

      const newArray = [...prev[section]];
      newArray[index] = {
        ...newArray[index],
        [field]: value,
      };

      return {
        ...prev,
        [section]: newArray,
      };
    });
  };

  const handleNestedArrayFieldChange = (
    section,
    parentIndex,
    nestedSection,
    nestedIndex,
    field,
    value
  ) => {
    setFormData((prev) => {
      if (!prev[section] || !Array.isArray(prev[section])) return prev;

      const newArray = [...prev[section]];
      if (!newArray[parentIndex][nestedSection]) {
        newArray[parentIndex][nestedSection] = [];
      }

      if (!newArray[parentIndex][nestedSection][nestedIndex]) {
        newArray[parentIndex][nestedSection][nestedIndex] = {};
      }

      newArray[parentIndex][nestedSection][nestedIndex] = {
        ...newArray[parentIndex][nestedSection][nestedIndex],
        [field]: value,
      };

      return {
        ...prev,
        [section]: newArray,
      };
    });
  };

  const addNewItem = (section) => {
    setFormData((prev) => ({
      ...prev,
      [section]: [...prev[section], { ...defaultItems[section] }],
    }));
  };

  const addNestedItem = (section, parentIndex, nestedSection) => {
    setFormData((prev) => {
      const newArray = [...prev[section]];

      if (!newArray[parentIndex][nestedSection]) {
        newArray[parentIndex][nestedSection] = [];
      }

      newArray[parentIndex][nestedSection] = [
        ...newArray[parentIndex][nestedSection],
        { ...defaultItems[nestedSection] },
      ];

      return {
        ...prev,
        [section]: newArray,
      };
    });
  };

  const removeItem = (section, index) => {
    setFormData((prev) => ({
      ...prev,
      [section]: prev[section].filter((_, i) => i !== index),
    }));
  };

  const removeNestedItem = (
    section,
    parentIndex,
    nestedSection,
    nestedIndex
  ) => {
    setFormData((prev) => {
      const newArray = [...prev[section]];

      if (newArray[parentIndex][nestedSection].length > 1) {
        newArray[parentIndex][nestedSection] = newArray[parentIndex][
          nestedSection
        ].filter((_, i) => i !== nestedIndex);
      } else {
        displayToast(`At least one ${nestedSection} item is required`, "error");
        return prev;
      }

      return {
        ...prev,
        [section]: newArray,
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSavingTransaction(true);
    try {
      const submitData = {
        ...formData,
        dynamic_transaction: Array.isArray(formData.dynamic_transaction)
          ? formData.dynamic_transaction[0]
          : formData.dynamic_transaction,
        filter_data:
          !formData.filter_data ||
            formData.filter_data.length === 0 ||
            (formData.filter_data.length === 1 &&
              !formData.filter_data[0].field_name)
            ? null
            : formData.filter_data,
      };

      const response = id
        ? await updateTransaction(id, submitData)
        : await createTransaction(submitData);

      if (response && (response.id || response.success)) {
        displayToast(
          `Transaction ${id ? "updated" : "added"} successfully!`,
          "success"
        );
        navigate("/transactions");
      } else {
        displayToast(
          `Failed to ${id ? "update" : "add"} transaction: ${response?.message || response?.description || "Unknown error"
          }`,
          "error"
        );
      }
    } catch (error) {
      console.error(`Error ${id ? "updating" : "adding"} transaction:`, error);
      displayToast(
        `Error ${id ? "updating" : "adding"} transaction: ${error.message || "Unknown error"
        }`,
        "error"
      );
    } finally {
      setIsSavingTransaction(false);
    }
  };

  return (
    <div className="transaction-page">
      <div className="transaction-container">
        <h1 className="page-header">
          {isViewMode ? "👁️ View Transaction" : (id ? "Edit Transaction" : "Transaction Configuration")}
        </h1>
        {isViewMode && (
          <div className="view-mode-banner">
            <span>📖</span> You are viewing this transaction in read-only mode
          </div>
        )}

        <form onSubmit={handleSubmit} className="transaction-form">
          <fieldset disabled={isViewMode} className="transaction-fieldset-reset">
          <section className="form-card">
            <div className="card-header">
              <h2>Basic Information</h2>
              <span className="badge">Required</span>
            </div>
            <div className="card-body">
              <div className="input-grid">
                <div className="input-group">
                  <label htmlFor="tran_code">Transaction Code</label>
                  <input
                    type="text"
                    id="tran_code"
                    name="tran_code"
                    value={formData.tran_code}
                    onChange={handleBasicInfoChange}
                    required
                    placeholder="Enter transaction code"
                  />
                </div>
                <div className="input-group">
                  <label htmlFor="name">Transaction Name</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleBasicInfoChange}
                    required
                    placeholder="Enter transaction name"
                  />
                </div>
              </div>
              <div className="input-grid">
                <div className="input-group">
                  <label htmlFor="customer_type">Customer Type</label>
                  <CustomSelect
                    options={CUSTOMER_TYPES.map(type => ({ value: type, label: type.charAt(0).toUpperCase() + type.slice(1) }))}
                    value={formData.customer_type}
                    onChange={(e) => handleBasicInfoChange({ target: { name: "customer_type", value: e.target.value } })}
                    placeholder="Select Customer Type"
                  />
                </div>
                <div className="input-group">
                  <label htmlFor="name">Document File Name</label>
                  <input
                    type="text"
                    id="document_file_name"
                    name="document_file_name"
                    required
                    value={formData.document_file_name}
                    onChange={handleBasicInfoChange}
                    placeholder="Enter Document File Name"
                  />
                </div>
              </div>
              <div className="input-grid">
                <div className="input-group input-group-half">
                  <label htmlFor="name">Color Code</label>
                  <div className="color-code-container">
                    <button
                      ref={buttonRef}
                      className="color-button"
                      onClick={handleButtonClick}
                      onMouseDown={(e) => e.preventDefault()}
                    />
                    <input
                      type="text"
                      id="color_code"
                      name="color_code"
                      className="color-input"
                      value={inputValue}
                      onChange={handleColorInputChange}
                      onBlur={handleColorInputBlur}
                      onFocus={(e) => e.target.select()}
                      placeholder="Enter Color Code"
                    />
                    {isOpen && (
                      <div className="color-popover" ref={popover}>
                        <HexColorPicker
                          color={
                            /^#[0-9A-Fa-f]{6}$/.test(formData.color_code)
                              ? formData.color_code
                              : "#000000"
                          }
                          onChange={handleColorPickerChange}
                        />
                      </div>
                    )}
                  </div>
                </div>
                <div className="input-group input-group-half">
                  <label htmlFor="ref_code">Reference Code (REF NO)</label>
                  <input
                    type="text"
                    id="ref_code"
                    name="ref_code"
                    value={formData.ref_code || ""}
                    onChange={handleBasicInfoChange}
                    placeholder="Defaults to transaction code"
                  />
                </div>
              </div>
              <div className="checkbox-grid">
                <label className="checkbox-wrapper">
                  <input
                    type="checkbox"
                    name="is_auto_transaction_no"
                    checked={formData.is_auto_transaction_no}
                    onChange={handleBasicInfoChange}
                  />
                  <span className="checkbox-label">
                    Auto Transaction Number
                  </span>
                </label>
                <label className="checkbox-wrapper">
                  <input
                    type="checkbox"
                    name="is_back_dated_entry_allowed"
                    checked={formData.is_back_dated_entry_allowed}
                    onChange={handleBasicInfoChange}
                  />
                  <span className="checkbox-label">
                    Back Dated Entry Allowed
                  </span>
                </label>
                <label className="checkbox-wrapper">
                  <input
                    type="checkbox"
                    name="is_pick_up_mandatory"
                    checked={formData.is_pick_up_mandatory}
                    onChange={handleBasicInfoChange}
                  />
                  <span className="checkbox-label">Pick Up Mandatory</span>
                </label>
                <label className="checkbox-wrapper">
                  <input
                    type="checkbox"
                    name="is_approval_mandatory"
                    checked={formData.is_approval_mandatory}
                    onChange={handleBasicInfoChange}
                  />
                  <span className="checkbox-label">Approval Mandatory</span>
                </label>
              </div>
            </div>
          </section>

          <section className="form-card">
            <div className="card-header">
              <h2>Pick Up Transactions</h2>
              <span className="badge badge-secondary">Optional</span>
            </div>
            <div className="card-body">
              <div className="data-rows">
                {formData?.pick_up_transactions?.map((transaction, index) => (
                  <div key={index} className="data-row">
                    <div className="row-inputs">
                      <CustomSelect
                        options={transactionOptions.map(option => ({ value: option.code, label: `${option.code} - ${option.name}` }))}
                        value={transaction.tran_code || ""}
                        onChange={(e) => {
                          const selectedCode = e.target.value;
                          const selectedOption = transactionOptions.find(
                            (option) => option.code === selectedCode
                          );
                          if (selectedOption) {
                            handleArrayFieldChange(
                              "pick_up_transactions",
                              index,
                              "tran_code",
                              selectedOption.code
                            );
                            handleArrayFieldChange(
                              "pick_up_transactions",
                              index,
                              "tran_name",
                              selectedOption.name
                            );
                          }
                        }}
                        placeholder="Select Transaction Code"
                      />
                      <input
                        type="text"
                        placeholder="Transaction Name"
                        value={transaction.tran_name || ""}
                        readOnly
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => removeItem("pick_up_transactions", index)}
                      className="remove-btn"
                      title="Remove"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
              <button
                type="button"
                onClick={() => addNewItem("pick_up_transactions")}
                className="add-btn"
              >
                ＋ Add Transaction
              </button>
            </div>
          </section>

          <section className="form-card">
            <div className="card-header">
              <h2>Pick Up Data</h2>
              <span className="badge badge-secondary">Optional</span>
            </div>
            <div className="card-body">
              {formData?.pick_up_data?.map((pickUpData, pickUpIndex) => (
                <div key={pickUpIndex} className="pickup-data-container">
                  <div className="data-row">
                    <div className="row-inputs">
                      <CustomSelect
                        options={transactionOptions.map(option => ({ value: option.code, label: `${option.code} - ${option.name}` }))}
                        value={pickUpData.tran_code || ""}
                        onChange={(e) => {
                          const selectedCode = e.target.value;
                          const selectedOption = transactionOptions.find(
                            (option) => option.code === selectedCode
                          );
                          if (selectedOption) {
                            handleArrayFieldChange(
                              "pick_up_data",
                              pickUpIndex,
                              "tran_code",
                              selectedOption.code
                            );
                            handleArrayFieldChange(
                              "pick_up_data",
                              pickUpIndex,
                              "tran_name",
                              selectedOption.name
                            );
                          }
                        }}
                        placeholder="Select Transaction Code"
                      />
                      <input
                        type="text"
                        placeholder="Transaction Name"
                        value={pickUpData.tran_name || ""}
                        readOnly
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => removeItem("pick_up_data", pickUpIndex)}
                      className="remove-btn"
                      title="Remove"
                    >
                      ×
                    </button>
                  </div>

                  <div className="fields-section">
                    <div className="fields-header">
                      <h4>Head Fields</h4>
                    </div>
                    <div className="data-rows">
                      {pickUpData.head_fields &&
                        pickUpData.head_fields.map((headField, headIndex) => (
                          <div key={headIndex} className="data-row">
                            <div className="row-inputs">
                              <input
                                type="text"
                                placeholder="Field Name"
                                value={headField.field_name || ""}
                                onChange={(e) =>
                                  handleNestedArrayFieldChange(
                                    "pick_up_data",
                                    pickUpIndex,
                                    "head_fields",
                                    headIndex,
                                    "field_name",
                                    e.target.value
                                  )
                                }
                                required
                              />
                              <input
                                type="text"
                                placeholder="Copy To Field Name"
                                value={headField.copy_to_field_name || ""}
                                onChange={(e) =>
                                  handleNestedArrayFieldChange(
                                    "pick_up_data",
                                    pickUpIndex,
                                    "head_fields",
                                    headIndex,
                                    "copy_to_field_name",
                                    e.target.value
                                  )
                                }
                                required
                              />
                              <CustomSelect
                                options={[{ value: "", label: "Select Table Type" }, { value: "head", label: "Head" }, { value: "item", label: "Item" }]}
                                value={headField.copy_to_table_type || ""}
                                onChange={(e) =>
                                  handleNestedArrayFieldChange(
                                    "pick_up_data",
                                    pickUpIndex,
                                    "head_fields",
                                    headIndex,
                                    "copy_to_table_type",
                                    e.target.value
                                  )
                                }
                                placeholder="Select Table Type"
                              />
                            </div>
                            <button
                              type="button"
                              onClick={() =>
                                removeNestedItem(
                                  "pick_up_data",
                                  pickUpIndex,
                                  "head_fields",
                                  headIndex
                                )
                              }
                              className="remove-btn"
                              title="Remove"
                            >
                              ×
                            </button>
                          </div>
                        ))}
                    </div>
                    <button
                      type="button"
                      onClick={() =>
                        addNestedItem(
                          "pick_up_data",
                          pickUpIndex,
                          "head_fields"
                        )
                      }
                      className="add-btn"
                    >
                      ＋ Add Head Field
                    </button>
                  </div>

                  <div className="fields-section">
                    <div className="fields-header">
                      <h4>Item Fields</h4>
                    </div>
                    <div className="data-rows">
                      {pickUpData.item_fields &&
                        pickUpData.item_fields.map((itemField, itemIndex) => (
                          <div key={itemIndex} className="data-row">
                            <div className="row-inputs">
                              <input
                                type="text"
                                placeholder="Field Name"
                                value={itemField.field_name || ""}
                                onChange={(e) =>
                                  handleNestedArrayFieldChange(
                                    "pick_up_data",
                                    pickUpIndex,
                                    "item_fields",
                                    itemIndex,
                                    "field_name",
                                    e.target.value
                                  )
                                }
                                required
                              />
                              <input
                                type="text"
                                placeholder="Copy To Field Name"
                                value={itemField.copy_to_field_name || ""}
                                onChange={(e) =>
                                  handleNestedArrayFieldChange(
                                    "pick_up_data",
                                    pickUpIndex,
                                    "item_fields",
                                    itemIndex,
                                    "copy_to_field_name",
                                    e.target.value
                                  )
                                }
                                required
                              />
                              <CustomSelect
                                options={[{ value: "", label: "Select Table Type" }, { value: "head", label: "Head" }, { value: "item", label: "Item" }]}
                                value={itemField.copy_to_table_type || ""}
                                onChange={(e) =>
                                  handleNestedArrayFieldChange(
                                    "pick_up_data",
                                    pickUpIndex,
                                    "item_fields",
                                    itemIndex,
                                    "copy_to_table_type",
                                    e.target.value
                                  )
                                }
                                placeholder="Select Table Type"
                              />
                            </div>
                            <button
                              type="button"
                              onClick={() =>
                                removeNestedItem(
                                  "pick_up_data",
                                  pickUpIndex,
                                  "item_fields",
                                  itemIndex
                                )
                              }
                              className="remove-btn"
                              title="Remove"
                            >
                              ×
                            </button>
                          </div>
                        ))}
                    </div>
                    <button
                      type="button"
                      onClick={() =>
                        addNestedItem(
                          "pick_up_data",
                          pickUpIndex,
                          "item_fields"
                        )
                      }
                      className="add-btn"
                    >
                      ＋ Add Item Field
                    </button>
                  </div>
                </div>
              ))}
              <button
                type="button"
                onClick={() => addNewItem("pick_up_data")}
                className="add-btn"
              >
                ＋ Add Pick Up Data
              </button>
            </div>
          </section>

          <section className="form-card">
            <div className="card-header">
              <h2>Dynamic Transaction</h2>
              <span className="badge badge-secondary">Optional</span>
            </div>
            <div className="card-body">
              {formData?.dynamic_transaction?.map(
                (dynamicData, dynamicDataIndex) => (
                  <div key={dynamicDataIndex} className="pickup-data-container">
                    <div className="data-row">
                      <div className="row-inputs">
                        <CustomSelect
                          options={transactionOptions.map(option => ({ value: option.code, label: `${option.code} - ${option.name}` }))}
                          value={dynamicData.tran_code || ""}
                          onChange={(e) => {
                            const selectedCode = e.target.value;
                            const selectedOption = transactionOptions.find(
                              (option) => option.code === selectedCode
                            );
                            if (selectedOption) {
                              handleArrayFieldChange(
                                "dynamic_transaction",
                                dynamicDataIndex,
                                "tran_code",
                                selectedOption.code
                              );
                              handleArrayFieldChange(
                                "dynamic_transaction",
                                dynamicDataIndex,
                                "tran_name",
                                selectedOption.name
                              );
                            }
                          }}
                          placeholder="Select Transaction Code"
                        />
                        <input
                          type="text"
                          placeholder="Transaction Name"
                          value={dynamicData.tran_name || ""}
                          readOnly
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() =>
                          removeItem("dynamic_transaction", dynamicDataIndex)
                        }
                        className="remove-btn"
                        title="Remove"
                      >
                        ×
                      </button>
                    </div>

                    <div className="fields-section">
                      <div className="fields-header">
                        <h4>Head Fields</h4>
                      </div>
                      <div className="data-rows">
                        {dynamicData.head_fields &&
                          dynamicData.head_fields.map(
                            (headField, headIndex) => (
                              <div key={headIndex} className="data-row">
                                <div className="row-inputs">
                                  <input
                                    type="text"
                                    placeholder="Field Name"
                                    value={headField.field_name || ""}
                                    onChange={(e) =>
                                      handleNestedArrayFieldChange(
                                        "dynamic_transaction",
                                        dynamicDataIndex,
                                        "head_fields",
                                        headIndex,
                                        "field_name",
                                        e.target.value
                                      )
                                    }
                                    required
                                  />
                                  <input
                                    type="text"
                                    placeholder="Copy To Field Name"
                                    value={headField.copy_to_field_name || ""}
                                    onChange={(e) =>
                                      handleNestedArrayFieldChange(
                                        "dynamic_transaction",
                                        dynamicDataIndex,
                                        "head_fields",
                                        headIndex,
                                        "copy_to_field_name",
                                        e.target.value
                                      )
                                    }
                                    required
                                  />
                                  <CustomSelect
                                    options={[{ value: "", label: "Select Table Type" }, { value: "head", label: "Head" }, { value: "item", label: "Item" }]}
                                    value={headField.copy_to_table_type || ""}
                                    onChange={(e) =>
                                      handleNestedArrayFieldChange(
                                        "dynamic_transaction",
                                        dynamicDataIndex,
                                        "head_fields",
                                        headIndex,
                                        "copy_to_table_type",
                                        e.target.value
                                      )
                                    }
                                    placeholder="Select Table Type"
                                  />
                                </div>
                                <button
                                  type="button"
                                  onClick={() =>
                                    removeNestedItem(
                                      "dynamic_transaction",
                                      dynamicDataIndex,
                                      "head_fields",
                                      headIndex
                                    )
                                  }
                                  className="remove-btn"
                                  title="Remove"
                                >
                                  ×
                                </button>
                              </div>
                            )
                          )}
                      </div>
                      <button
                        type="button"
                        onClick={() =>
                          addNestedItem(
                            "dynamic_transaction",
                            dynamicDataIndex,
                            "head_fields"
                          )
                        }
                        className="add-btn"
                      >
                        ＋ Add Head Field
                      </button>
                    </div>

                    <div className="fields-section">
                      <div className="fields-header">
                        <h4>Item Fields</h4>
                      </div>
                      <div className="data-rows">
                        {dynamicData.item_fields &&
                          dynamicData.item_fields.map(
                            (itemField, itemIndex) => (
                              <div key={itemIndex} className="data-row">
                                <div className="row-inputs">
                                  <input
                                    type="text"
                                    placeholder="Field Name"
                                    value={itemField.field_name || ""}
                                    onChange={(e) =>
                                      handleNestedArrayFieldChange(
                                        "dynamic_transaction",
                                        dynamicDataIndex,
                                        "item_fields",
                                        itemIndex,
                                        "field_name",
                                        e.target.value
                                      )
                                    }
                                    required
                                  />
                                  <input
                                    type="text"
                                    placeholder="Copy To Field Name"
                                    value={itemField.copy_to_field_name || ""}
                                    onChange={(e) =>
                                      handleNestedArrayFieldChange(
                                        "dynamic_transaction",
                                        dynamicDataIndex,
                                        "item_fields",
                                        itemIndex,
                                        "copy_to_field_name",
                                        e.target.value
                                      )
                                    }
                                    required
                                  />
                                  <CustomSelect
                                    options={[{ value: "", label: "Select Table Type" }, { value: "head", label: "Head" }, { value: "item", label: "Item" }]}
                                    value={itemField.copy_to_table_type || ""}
                                    onChange={(e) =>
                                      handleNestedArrayFieldChange(
                                        "dynamic_transaction",
                                        dynamicDataIndex,
                                        "item_fields",
                                        itemIndex,
                                        "copy_to_table_type",
                                        e.target.value
                                      )
                                    }
                                    placeholder="Select Table Type"
                                  />
                                </div>
                                <button
                                  type="button"
                                  onClick={() =>
                                    removeNestedItem(
                                      "dynamic_transaction",
                                      dynamicDataIndex,
                                      "item_fields",
                                      itemIndex
                                    )
                                  }
                                  className="remove-btn"
                                  title="Remove"
                                >
                                  ×
                                </button>
                              </div>
                            )
                          )}
                      </div>
                      <button
                        type="button"
                        onClick={() =>
                          addNestedItem(
                            "dynamic_transaction",
                            dynamicDataIndex,
                            "item_fields"
                          )
                        }
                        className="add-btn"
                      >
                        ＋ Add Item Field
                      </button>
                    </div>
                  </div>
                )
              )}
              <button
                type="button"
                onClick={() => addNewItem("dynamic_transaction")}
                className="add-btn"
              >
                ＋ Add Dynamic Transaction
              </button>
            </div>
          </section>

          <section className="form-card">
            <div className="card-header">
              <h2>Head View Fields</h2>
              <span className="badge badge-secondary">Optional</span>
            </div>
            <div className="card-body">
              <div className="data-rows">
                {formData?.head_view_fields?.map((field, index) => (
                  <div key={index} className="data-row">
                    <div className="row-inputs">
                      <input
                        type="text"
                        placeholder="Field Name"
                        value={field.field_name || ""}
                        onChange={(e) =>
                          handleArrayFieldChange(
                            "head_view_fields",
                            index,
                            "field_name",
                            e.target.value
                          )
                        }
                        required
                      />
                      <input
                        type="text"
                        placeholder="Display Name"
                        value={field.display_name || ""}
                        onChange={(e) =>
                          handleArrayFieldChange(
                            "head_view_fields",
                            index,
                            "display_name",
                            e.target.value
                          )
                        }
                        required
                      />
                      <input
                        type="number"
                        placeholder="Flex Value"
                        value={field.flex === 0 ? "" : field.flex}
                        onChange={(e) =>
                          handleArrayFieldChange(
                            "head_view_fields",
                            index,
                            "flex",
                            e.target.value === ""
                              ? 0
                              : parseInt(e.target.value, 10)
                          )
                        }
                        min="0"
                      />
                      <label className="checkbox-wrapper inline-checkbox">
                        <input
                          type="checkbox"
                          checked={field.is_internal || false}
                          onChange={(e) =>
                            handleArrayFieldChange(
                              "head_view_fields",
                              index,
                              "is_internal",
                              e.target.checked
                            )
                          }
                        />
                        <span className="checkbox-label">Internal Field</span>
                      </label>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeItem("head_view_fields", index)}
                      className="remove-btn"
                      title="Remove"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
              <button
                type="button"
                onClick={() => addNewItem("head_view_fields")}
                className="add-btn"
              >
                ＋ Add Head View Field
              </button>
            </div>
          </section>

          <section className="form-card">
            <div className="card-header">
              <h2>Pick Up Head View Fields</h2>
              <span className="badge badge-secondary">Optional</span>
            </div>
            <div className="card-body">
              <div className="data-rows">
                {formData?.pick_up_head_view_fields?.map((field, index) => (
                  <div key={index} className="data-row">
                    <div className="row-inputs">
                      <input
                        type="text"
                        placeholder="Field Name"
                        value={field.field_name || ""}
                        onChange={(e) =>
                          handleArrayFieldChange(
                            "pick_up_head_view_fields",
                            index,
                            "field_name",
                            e.target.value
                          )
                        }
                        required
                      />
                      <input
                        type="text"
                        placeholder="Display Name"
                        value={field.display_name || ""}
                        onChange={(e) =>
                          handleArrayFieldChange(
                            "pick_up_head_view_fields",
                            index,
                            "display_name",
                            e.target.value
                          )
                        }
                        required
                      />
                      <label className="checkbox-wrapper inline-checkbox">
                        <input
                          type="checkbox"
                          checked={field.is_internal || false}
                          onChange={(e) =>
                            handleArrayFieldChange(
                              "pick_up_head_view_fields",
                              index,
                              "is_internal",
                              e.target.checked
                            )
                          }
                        />
                        <span className="checkbox-label">Internal Field</span>
                      </label>
                    </div>
                    <button
                      type="button"
                      onClick={() =>
                        removeItem("pick_up_head_view_fields", index)
                      }
                      className="remove-btn"
                      title="Remove"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
              <button
                type="button"
                onClick={() => addNewItem("pick_up_head_view_fields")}
                className="add-btn"
              >
                ＋ Add Pick Up Head View Field
              </button>
            </div>
          </section>

          <section className="form-card">
            <div className="card-header">
              <h2>Pick Up Item View Fields</h2>
              <span className="badge badge-secondary">Optional</span>
            </div>
            <div className="card-body">
              <div className="data-rows">
                {formData?.pick_up_item_view_fields?.map((field, index) => (
                  <div key={index} className="data-row">
                    <div className="row-inputs">
                      <input
                        type="text"
                        placeholder="Field Name"
                        value={field.field_name || ""}
                        onChange={(e) =>
                          handleArrayFieldChange(
                            "pick_up_item_view_fields",
                            index,
                            "field_name",
                            e.target.value
                          )
                        }
                        required
                      />
                      <input
                        type="text"
                        placeholder="Display Name"
                        value={field.display_name || ""}
                        onChange={(e) =>
                          handleArrayFieldChange(
                            "pick_up_item_view_fields",
                            index,
                            "display_name",
                            e.target.value
                          )
                        }
                        required
                      />
                      <label className="checkbox-wrapper inline-checkbox">
                        <input
                          type="checkbox"
                          checked={field.is_internal || false}
                          onChange={(e) =>
                            handleArrayFieldChange(
                              "pick_up_item_view_fields",
                              index,
                              "is_internal",
                              e.target.checked
                            )
                          }
                        />
                        <span className="checkbox-label">Internal Field</span>
                      </label>
                    </div>
                    <button
                      type="button"
                      onClick={() =>
                        removeItem("pick_up_item_view_fields", index)
                      }
                      className="remove-btn"
                      title="Remove"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
              <button
                type="button"
                onClick={() => addNewItem("pick_up_item_view_fields")}
                className="add-btn"
              >
                ＋ Add Pick Up Item View Field
              </button>
            </div>
          </section>

          <section className="form-card">
            <div className="card-header">
              <h2>Filter Data</h2>
              <span className="badge badge-secondary">Optional</span>
            </div>
            <div className="card-body">
              <div className="data-rows">
                {(formData?.filter_data || []).map((filter, index) => (
                  <div key={index} className="data-row">
                    <div className="row-inputs">
                      <CustomSelect
                        options={[{ value: "", label: "Select Table Type" }, { value: "head", label: "Head" }, { value: "item", label: "Item" }]}
                        value={filter.table_type || ""}
                        onChange={(e) =>
                          handleArrayFieldChange(
                            "filter_data",
                            index,
                            "table_type",
                            e.target.value
                          )
                        }
                        placeholder="Select Table Type"
                      />

                      <input
                        type="text"
                        placeholder="Field Name"
                        value={filter.field_name || ""}
                        onChange={(e) =>
                          handleArrayFieldChange(
                            "filter_data",
                            index,
                            "field_name",
                            e.target.value
                          )
                        }
                        required
                      />
                      <input
                        type="text"
                        placeholder="Title"
                        value={filter.title || ""}
                        onChange={(e) =>
                          handleArrayFieldChange(
                            "filter_data",
                            index,
                            "title",
                            e.target.value
                          )
                        }
                        required
                      />
                      <CustomSelect
                        options={[
                          { value: "=", label: "Equal (=)" },
                          { value: "!=", label: "Not Equal (!=)" },
                          { value: ">", label: "Greater Than (>)" },
                          { value: "<", label: "Less Than (<)" },
                          { value: ">=", label: "Greater Than or Equal (>=)" },
                          { value: "<=", label: "Less Than or Equal (<=)" }
                        ]}
                        value={filter.operation || "="}
                        onChange={(e) =>
                          handleArrayFieldChange(
                            "filter_data",
                            index,
                            "operation",
                            e.target.value
                          )
                        }
                        placeholder="Select Operation"
                      />
                      <input
                        type="number"
                        placeholder="Order No"
                        value={filter.order_no ?? 1}
                        onChange={(e) =>
                          handleArrayFieldChange(
                            "filter_data",
                            index,
                            "order_no",
                            parseInt(e.target.value)
                          )
                        }
                        min="1"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => removeItem("filter_data", index)}
                      className="remove-btn"
                      title="Remove"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
              <button
                type="button"
                onClick={() => addNewItem("filter_data")}
                className="add-btn"
              >
                ＋ Add Filter
              </button>
            </div>
          </section>
          </fieldset>

          <div className="form-actions">
            {isViewMode ? (
              <>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => navigate("/transactions")}
                >
                  ← Back to List
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => navigate(`/transactions/edit/${id}`, { state: { transaction: formData } })}
                >
                  ✏️ Edit Transaction
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => navigate("/transactions")}
                >
                  ✕ Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  {isSavingTransaction ? (
                    <span className="loader"></span>
                  ) : (
                    "✓ Save Transaction"
                  )}
                </button>
              </>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default Transaction;
